import { DecisionAction, DecisionDetail } from "../../../models/home";
import { HomeDecisionActionUxService, DecisionActionPresentation } from "../../../services/home-ia/home-decision-action-ux.service";
import {
  DecisionConfidenceDisplay, DecisionMissingInformationDisplay, DecisionPriorityDisplay,
  DecisionRiskDisplay, DecisionTechnicalEntry, HomeDecisionDisplayUxService
} from "../../../services/home-ia/home-decision-display-ux.service";
import { HomeDecisionDetailI18nService } from "../../../services/home-ia/home-decision-detail-i18n.service";
import { LanguageService } from "../../../services/language/language.service";
import { DecisionActivityDisplay, DecisionTechnicalDisplay, InspectionTarget } from "./home-decision-detail.types";

export interface HomeDecisionDetailViewContext {
  detail: DecisionDetail | null;
  selectedActionKey: string | null;
  actionLoading: boolean;
  technicalTraceExpanded: boolean;
  inspectionTarget: InspectionTarget;
}

/** Builds presentation models from immutable detail view inputs. */
export class HomeDecisionDetailViewModelBuilder {
  constructor(
    private readonly lang: LanguageService,
    private readonly i18n: HomeDecisionDetailI18nService,
    private readonly actionUx: HomeDecisionActionUxService,
    private readonly displayUx: HomeDecisionDisplayUxService
  ) {}

  actionPresentation(context: HomeDecisionDetailViewContext): DecisionActionPresentation {
    return this.actionUx.getDetailActionPresentation(context.detail, context.selectedActionKey);
  }
  actionDisabledReason(context: HomeDecisionDetailViewContext): string | null {
    return this.i18n.translateActionDisabledReason(this.actionPresentation(context).disabledReason);
  }
  showPrimaryAction(context: HomeDecisionDetailViewContext): boolean {
    const presentation = this.actionPresentation(context);
    return !!presentation.primaryLabel && presentation.primaryMode !== "none";
  }
  primaryActionLabel(context: HomeDecisionDetailViewContext): string | null {
    const presentation = this.actionPresentation(context);
    const translated = this.i18n.translateActionLabel(presentation.action);
    if (presentation.primaryMode === "execute" && translated) return translated;
    if (context.detail?.decision?.availableActions !== undefined || presentation.primaryMode !== "inspect") {
      return translated ?? presentation.primaryLabel;
    }
    const key = context.inspectionTarget ? `home.decisionDetail.inspectCta.${context.inspectionTarget}` : "home.decisionDetail.inspectCta.generic";
    return this.lang.translate(key);
  }
  inspectActionHint(context: HomeDecisionDetailViewContext): string | null {
    return this.actionPresentation(context).primaryMode === "inspect" ? this.lang.translate("home.decisionDetail.inspectHint") : null;
  }
  isPrimaryActionDisabled(context: HomeDecisionDetailViewContext): boolean {
    return context.actionLoading || this.actionPresentation(context).primaryMode === "disabled";
  }
  availableActions(detail: DecisionDetail | null): readonly DecisionAction[] {
    return this.actionUx.getOperationalActions(detail?.decision ?? null);
  }
  selectedAction(context: HomeDecisionDetailViewContext): DecisionAction | null {
    const actions = this.availableActions(context.detail);
    return (context.selectedActionKey
      ? actions.find((action) => action.actionKey === context.selectedActionKey)
      : actions[0]) ?? null;
  }
  secondaryActions(detail: DecisionDetail | null): readonly DecisionAction[] { return this.actionUx.getSecondaryActions(detail?.decision ?? null); }
  remediationActions(detail: DecisionDetail | null): readonly DecisionAction[] { return this.actionUx.getRemediationActions(detail?.decision ?? null); }
  dangerActions(detail: DecisionDetail | null): readonly DecisionAction[] { return this.actionUx.getDangerActions(detail?.decision ?? null); }
  disabledActions(detail: DecisionDetail | null): readonly DecisionAction[] { return this.actionUx.getDisabledActions(detail?.decision ?? null); }
  selectedActionNeedsForm(context: HomeDecisionDetailViewContext): boolean { return this.actionUx.requiresForm(this.selectedAction(context)); }
  selectedActionUnavailableReason(context: HomeDecisionDetailViewContext): string | null {
    const action = this.selectedAction(context);
    if (!action) return null;
    if (this.actionUx.requiresForm(action) && !action.inputSchema) return this.lang.translate("home.decisionDetail.actionNeedsForm");
    return this.i18n.translateActionDisabledReason(action.disabledReason) ?? null;
  }
  technicalEntries(detail: DecisionDetail | null): readonly DecisionTechnicalEntry[] { return this.displayUx.getTechnicalEntries(detail); }
  technicalEntryDisplays(detail: DecisionDetail | null): readonly DecisionTechnicalDisplay[] {
    return this.technicalEntries(detail).map((entry) => ({
      label: entry.label,
      value: this.i18n.translateTechnicalValue(entry.key, entry.value),
      multiline: this.i18n.isTechnicalValueMultiline(entry.value)
    }));
  }
  priorityDisplay(detail: DecisionDetail | null): DecisionPriorityDisplay {
    const decision = detail?.decision;
    return this.displayUx.getPriorityDisplay(decision?.priority ?? { score: 0 }, decision?.state ?? "waiting", decision?.dueAt);
  }
  riskDisplay(detail: DecisionDetail | null): DecisionRiskDisplay {
    const decision = detail?.decision;
    return this.displayUx.getRiskDisplay(decision?.risk ?? {}, decision?.state ?? "waiting");
  }
  confidenceDisplay(detail: DecisionDetail | null): DecisionConfidenceDisplay {
    return this.displayUx.getConfidenceDisplay(detail?.decision?.confidence ?? { overall: 0, warnings: [] });
  }
  missingInformationDisplays(detail: DecisionDetail | null): readonly DecisionMissingInformationDisplay[] {
    return (detail?.decision?.missingInformation ?? []).map((item) => {
      const display = this.displayUx.getMissingInformationDisplay(item);
      return {
        ...display,
        label: this.i18n.translateActionFieldLabel(null, item.key ?? display.label, display.label),
        blockingReason: this.i18n.translateMissingInformationReason(display.blockingReason) ?? display.blockingReason
      };
    });
  }
  translatedDecisionReason(detail: DecisionDetail | null): string | null {
    return this.i18n.translateDecisionReason(detail?.decision?.shortReason);
  }
  translatedActionSummaryLabel(context: HomeDecisionDetailViewContext): string {
    const presentation = this.actionPresentation(context);
    return this.i18n.translateActionSummaryLabel(presentation.summaryLabel, presentation.action);
  }
  translatedActionSummaryCopy(context: HomeDecisionDetailViewContext): string {
    const presentation = this.actionPresentation(context);
    return this.i18n.translateActionSummaryCopy(presentation.summaryCopy, presentation.action);
  }
  translatedCompletionCriteria(context: HomeDecisionDetailViewContext): readonly string[] {
    const action = this.selectedAction(context);
    return (action?.completionCriteria ?? []).map((criterion, index) => this.i18n.translateActionCriterion(action, criterion, index));
  }
  translatedSelectedActionDescription(context: HomeDecisionDetailViewContext): string {
    return this.i18n.translateActionDescription(this.selectedAction(context)) ?? this.lang.translate("home.decisionDetail.availableActionsCopy");
  }
  historyPreviewDisplays(detail: DecisionDetail | null): readonly DecisionActivityDisplay[] {
    return (detail?.activityPreview ?? []).map((activity) => ({
      title: this.i18n.translateHistoryTitle(activity),
      narrative: this.i18n.translateHistoryNarrative(activity),
      occurredAt: activity.occurredAt
    }));
  }
  technicalTraceToggleLabel(expanded: boolean): string {
    return this.lang.translate(expanded ? "home.decisionDetail.technical.hide" : "home.decisionDetail.technical.show");
  }
}
